import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmojiInput } from '../../../common/utils/CommonUtils';
import { PATTERN_REGISTRY_FORM_PARAM } from '../../constants';
import { IPatternRegistryValidationModel, PatternRegistryFormData } from '../../models';

/**
 * Custom hook to handle Pattern Registry Form Inputs
 */
export const usePatternRegistryForm = () => {
  const [formData, setFormParamValues] = useState(new PatternRegistryFormData());
  const { t } = useTranslation();
  const PATTERN_REGISTRY_ERRORS = {
    invalidPatternRegistryName: t('patternRegErrors.invalidPatternRegistryName'),
    requiredPatternRegistryName: t('patternRegErrors.requiredPatternRegistryName'),
    invalidAuthor: t('patternRegErrors.invalidAuthor'),
    requiredAuthor: t('patternRegErrors.requiredAuthor'),
    invalidOccurrences: t('patternRegErrors.invalidOccurrences'),
    requiredOccurrences: t('patternRegErrors.requiredOccurrences'),
    invalidTimeToLive: t('patternRegErrors.invalidTimeToLive'),
    requiredTimeToLive: t('patternRegErrors.requiredTimeToLive'),
    invalidUniqueId: t('patternRegErrors.invalidUniqueId'),
    requiredUniqueId: t('patternRegErrors.requiredUniqueId'),
    invalidFDName: t('patternRegErrors.invalidFDName'),
    requiredFDName: t('patternRegErrors.requiredFDName'),
    invalidFDValue: t('patternRegErrors.invalidFDValue'),
    requiredFDValue: t('patternRegErrors.requiredFDValue'),
    invalidCPValue: t('patternRegErrors.invalidCPValue'),
    requiredCPValue: t('patternRegErrors.requiredCPValue'),
    invalidFieldCondition: t('patternRegErrors.invalidFieldCondition'),
    requiredFieldN: t('patternRegErrors.requiredFieldN'),
    invalidFieldN: t('patternRegErrors.invalidFieldN'),
  };
  const checkRegexValidity = (name: string, value: string) => {
    const keyName = name as keyof IPatternRegistryValidationModel;
    const expString = formData[keyName].expression;
    const regExp = new RegExp(expString ?? '');

    const regExCondition = formData[name as keyof IPatternRegistryValidationModel] && !regExp.test(value.toLowerCase());
    if (value.length < 1 || regExCondition) {
      setFormParamValues((previousState: IPatternRegistryValidationModel) => setFormElementStatus(previousState, keyName, value, null, false, true));
    } else {
      setFormParamValues((previousState: IPatternRegistryValidationModel) => setFormElementStatus(previousState, keyName, value, null, true, true));
    }
  };

  const fetchRequiredError = (dataName: string) => {
    switch (dataName) {
      case PATTERN_REGISTRY_FORM_PARAM.patternName:
        return PATTERN_REGISTRY_ERRORS.requiredPatternRegistryName;
      case PATTERN_REGISTRY_FORM_PARAM.occurrences:
        return PATTERN_REGISTRY_ERRORS.requiredOccurrences;
      case PATTERN_REGISTRY_FORM_PARAM.timeToLive:
        return PATTERN_REGISTRY_ERRORS.requiredTimeToLive;
      case PATTERN_REGISTRY_FORM_PARAM.uniqueId:
        return PATTERN_REGISTRY_ERRORS.requiredUniqueId;
      case PATTERN_REGISTRY_FORM_PARAM.fieldDefName:
        return PATTERN_REGISTRY_ERRORS.requiredFDName;
      case PATTERN_REGISTRY_FORM_PARAM.fieldDefValue:
        return PATTERN_REGISTRY_ERRORS.requiredFDValue;
      case PATTERN_REGISTRY_FORM_PARAM.fieldDefConversionPattern:
        return PATTERN_REGISTRY_ERRORS.requiredCPValue;
      case PATTERN_REGISTRY_FORM_PARAM.fieldN:
        return PATTERN_REGISTRY_ERRORS.requiredFieldN;
      default:
        return '';
    }
  };

  const fetchInvalidError = (dataName: string) => {
    switch (dataName) {
      case PATTERN_REGISTRY_FORM_PARAM.patternName:
        return PATTERN_REGISTRY_ERRORS.invalidPatternRegistryName;
      case PATTERN_REGISTRY_FORM_PARAM.occurrences:
        return PATTERN_REGISTRY_ERRORS.invalidOccurrences;
      case PATTERN_REGISTRY_FORM_PARAM.timeToLive:
        return PATTERN_REGISTRY_ERRORS.invalidTimeToLive;
      case PATTERN_REGISTRY_FORM_PARAM.uniqueId:
        return PATTERN_REGISTRY_ERRORS.invalidUniqueId;
      case PATTERN_REGISTRY_FORM_PARAM.fieldN:
        return PATTERN_REGISTRY_ERRORS.invalidFieldN;
      case PATTERN_REGISTRY_FORM_PARAM.fieldDefName:
        return PATTERN_REGISTRY_ERRORS.invalidFDName;
      case PATTERN_REGISTRY_FORM_PARAM.fieldDefValue:
        return PATTERN_REGISTRY_ERRORS.invalidFDValue;
      case PATTERN_REGISTRY_FORM_PARAM.fieldDefConversionPattern:
        return PATTERN_REGISTRY_ERRORS.invalidCPValue;
      case PATTERN_REGISTRY_FORM_PARAM.fieldCondition:
        return PATTERN_REGISTRY_ERRORS.invalidFieldCondition;
      default:
        return '';
    }
  };

  const getFormParam = (previousState: IPatternRegistryValidationModel, name: string, currLength: number, maxChars: number, value: string) => {
    const data = structuredClone(previousState);
    const keyName = name as keyof IPatternRegistryValidationModel;
    const dataName = data[keyName].name;

    if (currLength === maxChars) {
      data[keyName].isValid = false;
      data[keyName].value = value;
      data[keyName].isTouched = true;
      data[keyName].error = String(t(
        'pattern_registry_form.err_max_char_limit',
        { replace: { maxChars: maxChars - 1 } }
      ));
    } else if (data[keyName].value === '') {
      data[keyName].error = fetchRequiredError(dataName);
    } else {
      data[keyName].error = fetchInvalidError(dataName);
    }
    return data;
  };

  /**
 * method to set form element
 */
  const setFormElementStatus = (previousState: IPatternRegistryValidationModel, keyName: string, value: string, error: string | null, isValid: boolean, isTouched: boolean) => {
    const data = structuredClone(previousState);
    const key = keyName as keyof IPatternRegistryValidationModel;
    data[key].isValid = isValid;
    data[key].value = value;
    data[key].isTouched = isTouched;
    data[key].error = error ?? '';
    return data;
  };

  /**
 * method to handle form inputs
 */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (isEmojiInput(value)) {
      return;
    }
    const maxChars = event.target.maxLength > -1 ? event.target.maxLength : Number.MAX_SAFE_INTEGER;
    const currLength = event.target.value.length;
    if (formData[name as keyof IPatternRegistryValidationModel].expression) {
      checkRegexValidity(name, value);
    }
    setFormParamValues((previousState: IPatternRegistryValidationModel) => getFormParam(previousState, name, currLength, maxChars, value));
  };

  return {
    handleInputChange,
    formData,
    setFormParamValues,
    getFormParam
  };
};
